/**
 * Row reducers for the CPT table's visit-count cells
 * ("Total Visits", "Remaining", "Completed").
 * @module updateRowData/visitReducers
 */
import { CptCodeWithDetailsKeys } from "../../../../../../enums/tableColumnKeys";
import {
  checkIfNumVisitsMatchesRemainingAndCompleted,
  checkIfStringIsValidNumber,
} from "../computeErrorStates";
import {
  checkIfEnteredValueIsGreaterThanZero,
  getAuthStatusFlags,
} from "./sharedHelpers";

/**
 * Reducer for edits to the "Remaining" visits cell.
 *
 * Validates the entered value (positive, numeric) when the auth status is
 * on-file, and re-checks the total-visits consistency against remaining and
 * completed counts.
 *
 * @param rowDataToUpdate - Existing row snapshot.
 * @param updatedData - New remaining-visits value.
 * @returns New row object with updated RemainingVisits and Visits cells.
 */
export const updateRowDataForRemainingVisits = (
  rowDataToUpdate: any,
  updatedData: any,
) => {
  const { isAuthOnFile, isAuthByRisa } = getAuthStatusFlags(rowDataToUpdate);
  const hasError =
    isAuthOnFile || isAuthByRisa
      ? !checkIfEnteredValueIsGreaterThanZero(updatedData) ||
        !checkIfStringIsValidNumber(updatedData)
      : false;
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.RemainingVisits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits],
      hasError: isAuthOnFile ? hasError : false,
      data: updatedData,
    },
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError:
        isAuthOnFile || isAuthByRisa
          ? !checkIfNumVisitsMatchesRemainingAndCompleted(
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
              ),
              parseInt(updatedData),
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ??
                  "0",
              ),
            )
          : false,
    },
  };
};

/**
 * Reducer for edits to the "Completed" (visits allowed) cell.
 *
 * @param rowDataToUpdate - Existing row snapshot.
 * @param updatedData - New completed-visits value.
 * @returns New row object with updated VisitsAllowed and Visits cells.
 */
export const updateRowDataForVisitsAllowed = (
  rowDataToUpdate: any,
  updatedData: any,
) => {
  const hasError = !checkIfStringIsValidNumber(updatedData);
  const { isAuthOnFile, isAuthByRisa } = getAuthStatusFlags(rowDataToUpdate);
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.VisitsAllowed]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError:
        isAuthOnFile || isAuthByRisa
          ? !checkIfNumVisitsMatchesRemainingAndCompleted(
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
              ),
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ??
                  "0",
              ),
              parseInt(updatedData ?? "0"),
            )
          : false,
    },
  };
};

/**
 * Reducer for edits to the "Total Visits" cell.
 *
 * @param rowDataToUpdate - Existing row snapshot.
 * @param updatedData - New total-visits value.
 * @returns New row object with updated Visits cell.
 */
export const updateRowDataForVisits = (
  rowDataToUpdate: any,
  updatedData: any,
) => {
  const { isAuthOnFile, isAuthByRisa } = getAuthStatusFlags(rowDataToUpdate);
  const hasError =
    !checkIfStringIsValidNumber(updatedData) ||
    !checkIfNumVisitsMatchesRemainingAndCompleted(
      parseInt(updatedData ?? "0"),
      parseInt(
        rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ?? "0",
      ),
      parseInt(
        rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ?? "0",
      ),
    );
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
  };
};
